import { useCallback } from 'react';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, useUpdateDocumentTags } from '../../hooks/useTags';
import type { TagDefinition } from '../../hooks/useTags';
import type { TagColor } from '@hofsync/design-system';
import type { DocumentData } from '../../hooks/useDocuments';

interface UseTagActionsOptions {
    feedData: DocumentData[];
    refreshDocuments: () => Promise<void>;
    updateDocumentTagsOptimistic?: (docId: string, tagIds: string[]) => void;
}

export interface UseTagActionsReturn {
    allTags: TagDefinition[] | undefined;
    handleToggleTag: (docId: string, tagId: string) => void;
    handleCreateTag: (docId: string, name: string, color: TagColor) => void;
    handleEditTag: (tagId: string, name: string, color: TagColor) => void;
    handleDeleteTag: (tagId: string) => void;
}

export function useTagActions({ feedData, refreshDocuments, updateDocumentTagsOptimistic }: UseTagActionsOptions): UseTagActionsReturn {
    const { data: allTags } = useTags();
    const createTagMutation = useCreateTag();
    const updateTagMutation = useUpdateTag();
    const deleteTagMutation = useDeleteTag();
    const updateDocTagsMutation = useUpdateDocumentTags();

    const handleToggleTag = useCallback(
        (docId: string, tagId: string) => {
            const doc = feedData.find((d) => d.doc_id === docId);
            const currentTagIds = doc?.tagIds || [];
            const newTagIds = currentTagIds.includes(tagId)
                ? currentTagIds.filter((id) => id !== tagId)
                : [...currentTagIds, tagId];

            // Optimistic update: immediately reflect in UI
            updateDocumentTagsOptimistic?.(docId, newTagIds);

            updateDocTagsMutation.mutate(
                { docId, tagIds: newTagIds },
                {
                    onError: () => {
                        // Rollback: revert to previous tags
                        updateDocumentTagsOptimistic?.(docId, currentTagIds);
                        refreshDocuments();
                    },
                }
            );
        },
        [feedData, updateDocTagsMutation, refreshDocuments, updateDocumentTagsOptimistic]
    );

    const handleCreateTag = useCallback(
        (docId: string, name: string, color: TagColor) => {
            createTagMutation.mutate(
                { name, color },
                {
                    onSuccess: (newTag) => {
                        // Auto-apply the new tag to the document
                        if (newTag?.id) {
                            handleToggleTag(docId, newTag.id);
                        }
                    },
                }
            );
        },
        [createTagMutation, handleToggleTag]
    );

    const handleEditTag = useCallback(
        (tagId: string, name: string, color: TagColor) => {
            updateTagMutation.mutate({ tagId, name, color });
        },
        [updateTagMutation]
    );

    const handleDeleteTag = useCallback(
        (tagId: string) => {
            deleteTagMutation.mutate(tagId, {
                onSuccess: () => refreshDocuments(),
            });
        },
        [deleteTagMutation, refreshDocuments]
    );

    return {
        allTags,
        handleToggleTag,
        handleCreateTag,
        handleEditTag,
        handleDeleteTag,
    };
}
